/**
 * The ops side of bank-transfer (ACH) refunds, in ronin-api.
 *
 * Same bearer-token pattern as modules/cash_refunds/api.ts. Every call is
 * admin-only on the server; this file just talks to it.
 */

const API_BASE =
  (import.meta.env.VITE_RONIN_API_BASE as string | undefined) ||
  "https://api.roninpos.app";

export type AchRefund = {
  guid: string;
  uid: string;
  amount: number;
  gross_amount: number;
  fee_amount: number;
  recipient_name: string | null;
  recipient_email: string | null;
  bank_account_type: string | null;
  bank_account_last4: string | null;
  created_at: string;
};

export type AchBatch = {
  guid: string;
  status: "draft" | "sent" | "canceled";
  file_name: string;
  batch_number: number;
  sec_code: string;
  entry_description: string;
  effective_date: string;
  total_amount: number;
  transaction_count: number;
  created_at: string;
  sent_at: string | null;
  canceled_at: string | null;
};

export type AchPending = {
  event: { id: number; name: string; slug: string | null };
  settings: {
    provider: string;
    secCode: string;
    entryDescription: string;
    effectiveDays: number;
    accountLast4: string | null;
    nextEffectiveDate: string;
  };
  /** Why an export would be refused right now. Empty means ready. */
  problems: string[];
  refunds: AchRefund[];
  totalAmount: number;
  batches: AchBatch[];
};

export type AchExport = {
  batch: AchBatch & { id: number };
  fileName: string;
  /** The file itself. Held in memory only — see downloadCsv. */
  csv: string;
  count: number;
  totalAmount: number;
  effectiveDate: string;
};

async function post<T>(body: unknown): Promise<T> {
  const token = localStorage.getItem("ron-auth");
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/cashRefund/ach`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch (err: any) {
    throw new Error(
      `Network error calling ${API_BASE}/api/cashRefund/ach: ${err?.message || err}`
    );
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || data?.error || `${res.status} ${res.statusText}`);
  }
  return data as T;
}

export const loadAchPending = (eventId: number) =>
  post<AchPending>({ op: "pending", eventId });

/**
 * Claims these refunds into a payment file and returns it. The refunds leave
 * the queue at this point but are NOT paid until the file is confirmed, so a
 * second export cannot pay the same person twice.
 */
export const exportAchBatch = (eventId: number, guids?: string[]) =>
  post<AchExport>({ op: "export", eventId, ...(guids?.length ? { guids } : {}) });

/** "The bank has the file": marks the refunds sent and wipes the bank details. */
export const confirmAchBatch = (batchGuid: string) =>
  post<{ batch: AchBatch; marked: number }>({ op: "confirm", batchGuid });

/** "That file never went": puts every refund in it back in the queue. */
export const cancelAchBatch = (batchGuid: string, reason?: string) =>
  post<{ batch: AchBatch; released: number }>({ op: "cancel", batchGuid, reason });

/**
 * Hands the file to the browser as a download. It is never written anywhere
 * else: it carries every payee's bank details, so the fewer copies the better.
 */
export function downloadCsv(fileName: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoke on the next tick: Safari has not finished reading it synchronously.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
